import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useRBAC } from "Components/Hooks/useRBAC";
import { hasRoleLevel, Permission, RoleLevel } from "helpers/utils/rbac";

const Navdata = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { canAny, userRoleLevel } = useRBAC();

  const [isDashboard, setIsDashboard] = useState(false);
  const [isForms, setIsForms] = useState(false);
  const [, setIscurrentState] = useState("Dashboard");

  const prevPathnameRef = useRef(location.pathname);

  // Master menu items
  const masterMenuItems = useMemo(
    () => [
      {
        id: "roles",
        label: "Roles",
        link: "/master/roles",
        parentId: "masters",
        permissions: [Permission.ROLES_READ],
      },
      {
        id: "user",
        label: "User",
        link: "/master/user",
        parentId: "masters",
        permissions: [Permission.USERS_READ],
      },
      {
        id: "employees",
        label: "Employees",
        link: "/master/employee",
        parentId: "masters",
        permissions: [],
        roleLevel: RoleLevel.ORGANIZATION_ADMIN,
      },
      {
        id: "locations",
        label: "Locations",
        link: "/master/locations",
        parentId: "masters",
        permissions: [],
        roleLevel: RoleLevel.ORGANIZATION_ADMIN,
      },
      {
        id: "amenities",
        label: "Amenities",
        link: "/master/amenities",
        parentId: "masters",
        permissions: [],
        roleLevel: RoleLevel.ORGANIZATION_ADMIN,
      },
      {
        id: "banks",
        label: "Banks",
        link: "/master/banks",
        parentId: "masters",
        permissions: [],
        roleLevel: RoleLevel.ORGANIZATION_ADMIN,
      },
      {
        id: "sources",
        label: "Sources",
        link: "/master/source",
        parentId: "masters",
        permissions: [],
        roleLevel: RoleLevel.ORGANIZATION_ADMIN,
      },
    ],
    [],
  );

  // Dashboard sub-menu items
  const dashboardMenuItems = useMemo(
    () => [
      {
        id: "leadPipelineReport",
        label: "Lead Pipeline",
        link: "/dashboard/lead-pipeline-report",
        parentId: "dashboard",
        permissions: [],
        roleLevel: RoleLevel.ORGANIZATION_ADMIN,
      },
      {
        id: "siteVisitDashboard",
        label: "Site Visit Scheduled",
        link: "/dashboard/site-visit-scheduled-dashboard",
        parentId: "dashboard",
        permissions: [],
        roleLevel: RoleLevel.ORGANIZATION_ADMIN,
      },
      {
        id: "rmHourlyDashboard",
        label: "RM Hourly Report",
        link: "/dashboard/rm-calling",
        parentId: "dashboard",
        permissions: [],
        roleLevel: RoleLevel.ORGANIZATION_ADMIN,
      },
    ],
    [],
  );

  // Filters
  const filteredMasterItems = useMemo(() => {
    return masterMenuItems.filter(item => {
      if (item.permissions && item.permissions.length > 0) {
        if (!canAny(item.permissions)) return false;
      }
      if (item.roleLevel !== undefined) {
        if (!hasRoleLevel(userRoleLevel, item.roleLevel)) return false;
      }
      return true;
    });
  }, [masterMenuItems, canAny, userRoleLevel]);

  const filteredDashboardItems = useMemo(() => {
    return dashboardMenuItems.filter(item => {
      if (item.permissions && item.permissions.length > 0) {
        if (!canAny(item.permissions)) return false;
      }
      if (item.roleLevel !== undefined) {
        if (!hasRoleLevel(userRoleLevel, item.roleLevel)) return false;
      }
      return true;
    });
  }, [dashboardMenuItems, canAny, userRoleLevel]);

  // Dashboard Click Logic
  const handleDashboardClick = useCallback(
    e => {
      setIsDashboard(prev => !prev);
      setIscurrentState("Dashboard");

      // Navigate to /dashboard if clicked on parent
      if (location.pathname !== "/dashboard" && !location.pathname.startsWith("/dashboard/")) {
        navigate("/dashboard");
      }
    },
    [location.pathname, navigate],
  );

  // Masters Click Logic
  const handleMastersToggle = useCallback(e => {
    if (e) e.preventDefault();
    setIsForms(prev => !prev);
    setIscurrentState("masters");
  }, []);

  // Sync state on route change
  useEffect(() => {
    const currentPathname = location.pathname;

    if (prevPathnameRef.current === currentPathname) return;
    prevPathnameRef.current = currentPathname;

    const isDashboardRoute = currentPathname.startsWith("/dashboard");
    const isMasterRoute = currentPathname.startsWith("/master");

    if (isDashboardRoute) {
      setIsDashboard(true);
      setIscurrentState("Dashboard");
    }

    if (isMasterRoute) {
      setIsForms(true);
      setIscurrentState("masters");
    }
  }, [location.pathname]);

  const menuItems = useMemo(
    () => [
      {
        label: "Menu",
        isHeader: true,
      },
      {
        id: "dashboard",
        label: "Dashboard",
        icon: "ri-dashboard-2-line",
        link: "/dashboard",
        show: true,
        click: handleDashboardClick,
        stateVariables: isDashboard,
        subItems: filteredDashboardItems,
      },
      {
        id: "projects",
        label: "Projects",
        icon: "ri-apps-2-line",
        link: "/project",
        show: canAny([Permission.PROJECTS_READ]),
      },
      {
        id: "developer",
        label: "Developer",
        icon: "bx bx-buildings",
        link: "/developer",
        show: canAny([Permission.BUILDERS_READ]),
      },
      {
        id: "inquiries",
        label: "Inquiries",
        icon: "ri-stack-line",
        link: "/inquiries",
        show: canAny([Permission.LEADS_READ]),
      },
      {
        id: "masters",
        label: "Masters",
        icon: "ri-file-list-3-line",
        link: "/#",
        show: filteredMasterItems.length > 0,
        click: handleMastersToggle,
        stateVariables: isForms,
        subItems: filteredMasterItems,
      },
    ],
    [
      isDashboard,
      isForms,
      filteredDashboardItems,
      filteredMasterItems,
      handleDashboardClick,
      handleMastersToggle,
      canAny,
    ],
  );

  const visibleMenuItems = useMemo(
    () => menuItems.filter(item => item.show !== false),
    [menuItems],
  );

  return visibleMenuItems;
};

export default Navdata;
